import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, ShoppingBag, BookOpen } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { resolvePhotoUrl } from "@/lib/api";

function ContactSellerLink({ className, iconClass }) {
    const { whats_app_number } = useSettings();
    if (!whats_app_number) return null;
    const href = `https://wa.me/${whats_app_number}?text=${encodeURIComponent("Hi")}`;
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label="Contact seller"
            title="Contact seller"
            className={className}
        >
            <svg
                className={iconClass}
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.852L.057 23.571a.5.5 0 0 0 .612.612l5.76-1.485A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.87 9.87 0 0 1-5.022-1.373l-.36-.214-3.733.961.993-3.648-.235-.374A9.861 9.861 0 0 1 2.118 12C2.118 6.54 6.54 2.118 12 2.118S21.882 6.54 21.882 12 17.46 21.882 12 21.882z" />
            </svg>
        </a>
    );
}

const items = [
    { to: "/", label: "Shop", icon: Home, testid: "nav-shop" },
    { to: "/checkout", label: "Cart", icon: ShoppingBag, testid: "nav-cart" },
];

// Small logo image shared by desktop & mobile headers
function StoreMark({ size = "h-9 w-9", testid = "store-logo" }) {
    const { logo_url, store_name } = useSettings();
    if (!logo_url) return null;
    return (
        <span
            className={`${size} rounded-xl overflow-hidden bg-surface/60 flex items-center justify-center shrink-0`}
        >
            <img
                data-testid={testid}
                src={resolvePhotoUrl(logo_url)}
                alt={store_name ? `${store_name} logo` : "Store logo"}
                className="w-full h-full object-contain"
            />
        </span>
    );
}

// Mobile: sticky top bar with logo + store name (visible below md)
export function MobileHeader() {
    const { store_name } = useSettings();
    return (
        <header
            data-testid="mobile-top-header"
            className="md:hidden sticky top-0 z-40 glass border-b border-border/40"
        >
            <div className="w-full max-w-md mx-auto px-4 py-3 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2 min-w-0">
                    <StoreMark size="h-8 w-8" testid="mobile-store-logo" />
                    <span
                        data-testid="mobile-store-name"
                        className="font-serif text-lg text-foreground truncate"
                    >
                        {store_name}
                    </span>
                </Link>
                <ContactSellerLink
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    iconClass="h-6 w-6"
                />
            </div>
        </header>
    );
}

// Mobile: fixed bottom navigation
export function BottomNav() {
    return (
        <nav
            data-testid="bottom-nav"
            className="md:hidden fixed bottom-0 left-1/2 -translate-x-1/2 z-50 glass border-t border-border/40 pb-safe pt-2 px-8 flex justify-between items-center gap-10 w-full max-w-md"
        >
            {items.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={testid}
                    className={({ isActive }) =>
                        `flex flex-col items-center gap-1 text-xs uppercase tracking-[0.15em] transition-colors ${
                            isActive
                                ? "text-primary font-semibold"
                                : "text-muted-foreground hover:text-foreground"
                        }`
                    }
                >
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                    {label}
                </NavLink>
            ))}
            <a
                href="/how-to-order.html"
                data-testid="nav-how-to-order"
                className="flex flex-col items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors"
            >
                <BookOpen className="h-5 w-5" strokeWidth={1.5} />
                How to Order
            </a>
        </nav>
    );
}

// Desktop: sticky top navigation bar
export function TopNav() {
    const { count } = useCart();
    const { store_name } = useSettings();
    return (
        <header
            data-testid="top-nav"
            className="hidden md:block sticky top-0 z-40 glass border-b border-border/40"
        >
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 min-w-0">
                    <StoreMark size="h-10 w-10" testid="desktop-store-logo" />
                    <span
                        data-testid="store-name"
                        className="font-serif text-2xl text-foreground truncate"
                    >
                        {store_name}
                    </span>
                </Link>
                <nav className="flex items-center gap-2">
                    {items.map(({ to, label, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={to === "/"}
                            data-testid={`top-${testid}`}
                            className={({ isActive }) =>
                                `relative px-4 py-2 rounded-full text-sm transition-colors ${
                                    isActive
                                        ? "bg-foreground text-background"
                                        : "text-foreground hover:bg-surface/70"
                                }`
                            }
                        >
                            {label}
                            {label === "Cart" && count > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1">
                                    {count}
                                </span>
                            )}
                        </NavLink>
                    ))}
                    <ContactSellerLink
                        className="ml-1 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-surface/70 transition-colors"
                        iconClass="h-5 w-5"
                    />
                    <a
                        href="/how-to-order.html"
                        className="ml-1 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-surface/70 transition-colors flex items-center gap-1.5 text-sm"
                        title="How to Order"
                    >
                        <BookOpen className="h-5 w-5" strokeWidth={1.5} />
                    </a>
                </nav>
            </div>
        </header>
    );
}
